/**
 * File: public_page.tsx
 * Description: Public-facing FAQ page where citizens can browse FAQ entries organised by category. Accessible at the root path "/".
 */
import { useState } from "react";
import { 
  Box,
  Typography,
} from "@mui/material";
import type { Category } from "@/interfaces/category";
import SearchArticles from "@/components/global/search_articles";
import Header from "@/components/global/header";
import SelectFilterArticleList from "@/components/public/select_filter_article_list";
import { useCategoriesContext } from "@/context/public/categories_context";
import RecentArticles from "@/components/public/recent_articles";
import BreadcrumbNavegation from "@/components/public/breadcrumb_navegation";

/**
 * Page component for the public FAQ interface.
 * Renders the citizen-facing view that lists FAQ categories and their articles.
 *
 * @returns {JSX.Element}
 */
function MainPage() {

  const {categories, isLoading} = useCategoriesContext()

  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  /**
   * Receives the filtered category subset from SearchArticles and stores it
   * so SelectFilterArticleList can render only the matching entries.
   *
   * @param {Category[]} filtered - The categories that match the current search selection.
   */
  function handleFilter(filtered: Category[]) {
    setFilteredCategories(filtered);
  }
  
return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
      <Header />
      <BreadcrumbNavegation items={[]}/>
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: { xs:'column-reverse', sm:'row', },
          justifyContent: { xs:'flex-end', sm:'normal', },
          flex: 1,
        }}
      >
        {
          isLoading ?
          <></>
          :
          <>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: { xs:1, sm:2, }, 
                width: { xs: "100%", sm:"50%", }, 
                paddingLeft: { xs: 1, sm:3, }, 
                paddingRight: { xs: 1, sm:3, },
                paddingTop: { xs: 2, sm:0, },
              }}
            >
              <Typography 
                variant="h5"
                sx={{ 
                  display: { xs: 'flex', sm:'inline' }, 
                  justifyContent: { xs: 'center', sm: 'normal'},
                }}
              >
                Artículos Recientes
              </Typography>
              <RecentArticles categories={categories}/>
            </Box>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: { xs:1, sm:2, }, 
                width: { xs: "100%", sm:"50%", }, 
                paddingLeft: { xs: 1.5, sm:3, }, 
                paddingRight: { xs: 1.5, sm:3, },
              }}
            >
              <Typography 
                variant="h5"
                sx={{ 
                  display: { xs: 'flex', sm:'inline' }, 
                  justifyContent: { xs: 'center', sm: 'normal'},
                }}
              >
                Buscar Artículo
              </Typography>
              <SearchArticles
                categories={categories}
                onFilter={handleFilter}
              />
              <SelectFilterArticleList categories={filteredCategories}/>
            </Box>
          </>
        }
      </Box>
    </Box>
  );
}

export default MainPage;
